/**
 * Event, Rally Lab, and Bear operational contracts (spec sections 19-20, 36,
 * 24-25, 33, 52, 73).
 *
 * Split out of api.ts to keep that file navigable. Re-exported from index.
 */

import { z } from 'zod';
import {
  bearPlanResponseSchema,
  confidenceLevelSchema,
  personalBearAnalysisSchema,
  scoreBreakdownSchema,
} from './api.js';

export const eventTypeSchema = z.enum([
  'BEAR_TRAP',
  'ALLIANCE_CHAMPIONSHIP',
  'SWORDLAND',
  'SANCTUARY',
  'FORTRESS',
  'CASTLE',
  'VIKING_VENGEANCE',
  'TRI_ALLIANCE_CLASH',
  'ALLIANCE_MOBILIZATION',
  'ALLIANCE_BRAWL',
  'KVK',
  'CUSTOM',
]);

export const attendanceStatusSchema = z.enum([
  'REGISTERED',
  'CONFIRMED',
  'ATTENDED',
  'LATE',
  'NO_SHOW',
  'COMPLETED_ROLE',
  'EXCUSED',
]);

export const rallyRoleSchema = z.enum([
  'BEAR_OFFENSE',
  'PVP_ATTACK',
  'PVP_GARRISON',
  'PVP_JOINER',
  'CASTLE_ATTACK',
  'CASTLE_GARRISON',
  'FORTRESS_ATTACK',
  'FORTRESS_GARRISON',
  'SANCTUARY_ATTACK',
  'SANCTUARY_GARRISON',
]);

/** Spec section 25: how far the bound snapshot sits from the event. */
export const contextQualitySchema = z.enum(['EXACT', 'NEAR', 'STALE', 'MISSING']);

// ---------------------------------------------------------------------------
// Events (spec sections 19-20)
// ---------------------------------------------------------------------------

export const eventSchema = z.object({
  id: z.string(),
  allianceId: z.string().nullable(),
  kingdomId: z.number().nullable(),
  eventType: eventTypeSchema,
  name: z.string().nullable(),
  startsAt: z.string().nullable(),
  endsAt: z.string().nullable(),
  status: z.enum(['PLANNED', 'OPEN', 'IN_PROGRESS', 'COMPLETED', 'CANCELLED']),
  ruleVersion: z.string().nullable(),
  metadata: z.record(z.unknown()),
  createdAt: z.string(),
});

export const eventParticipantSchema = z.object({
  playerId: z.string(),
  playerName: z.string().nullable(),
  available: z.boolean().nullable(),
  confirmed: z.boolean().nullable(),
  role: z.string().nullable(),
  team: z.string().nullable(),
  priority: z.number().nullable(),
  attendanceStatus: attendanceStatusSchema.nullable(),
  manualNotes: z.string().nullable(),
});

export const eventListResponseSchema = z.object({ events: z.array(eventSchema) });
export const eventResponseSchema = z.object({ event: eventSchema });
export const eventDetailResponseSchema = z.object({
  event: eventSchema,
  participants: z.array(eventParticipantSchema),
});
export const eventParticipantsUpdatedSchema = z.object({
  eventId: z.string(),
  participantCount: z.number(),
});

export const createEventSchema = z.object({
  eventType: eventTypeSchema,
  allianceId: z.string().optional(),
  kingdomId: z.number().optional(),
  name: z.string().optional(),
  startsAt: z.string().optional(),
  endsAt: z.string().optional(),
  metadata: z.record(z.unknown()).optional(),
});

// ---------------------------------------------------------------------------
// Rally Lab (spec section 36)
// ---------------------------------------------------------------------------

export const rallyRecommendRequestSchema = z.object({
  eventType: eventTypeSchema,
  side: z.enum(['ATTACK', 'DEFENSE']).default('ATTACK'),
  roster: z.array(z.string()).min(1).max(200),
  candidateLeadPlayerId: z.string().optional(),
  excludedPlayerIds: z.array(z.string()).optional(),
  joinerCount: z.number().int().min(1).max(50).optional(),
});

const rallyPickSchema = z.object({
  playerId: z.string(),
  playerName: z.string().nullable(),
  score: z.number().nullable(),
});

export const rallyLabResultSchema = z.object({
  /** Null when the requested side does not exist for this event type. */
  role: rallyRoleSchema.nullable(),
  recommendedLead: rallyPickSchema.nullable(),
  backupLead: rallyPickSchema.nullable(),
  recommendedLeaderHeroes: z.array(
    z.object({ heroId: z.number(), heroName: z.string().nullable() }),
  ),
  recommendedJoiners: z.array(
    rallyPickSchema.extend({
      captainHeroId: z.number().nullable(),
      captainHeroName: z.string().nullable(),
    }),
  ),
  /**
   * Null when no observed results support a composition. Spec sections 29/74
   * forbid presenting a default formation as a recommendation, so the UI must
   * render null as "no composition recommended" -- never as a fallback ratio.
   */
  troopComposition: z
    .object({ label: z.string(), samples: z.number(), normalized: z.number() })
    .nullable(),
  /** Who could not be ranked and why. */
  missingData: z.array(z.string()),
});

export const rallyRecommendResponseSchema = z.object({
  result: rallyLabResultSchema,
  confidence: z.number().min(0).max(1),
  confidenceLevel: confidenceLevelSchema,
  reasons: z.array(z.string()),
  warnings: z.array(z.string()),
  modelVersion: z.string(),
  ruleVersion: z.string().optional(),
  sourceDataAt: z.string(),
  recommendationRunId: z.string().nullable(),
});

// ---------------------------------------------------------------------------
// Bear entry responses (spec sections 24-25)
// ---------------------------------------------------------------------------

/**
 * Spec section 25. `contextAgeSeconds` is SIGNED: negative means the snapshot
 * predates the rally. Both directions matter -- a snapshot taken well after a
 * rally cannot describe the loadout used in it either.
 */
export const boundContextSchema = z.object({
  playerId: z.string(),
  playerSnapshotId: z.string().nullable(),
  heroGearSnapshotId: z.string().nullable(),
  contextTime: z.string().nullable(),
  contextQuality: contextQualitySchema,
  contextAgeSeconds: z.number().nullable(),
});

export const createdBearSessionSchema = z.object({
  session: z.object({
    id: z.string(),
    allianceId: z.string().nullable(),
    trapNumber: z.number().nullable(),
    startedAt: z.string().nullable(),
    entryStatus: z.enum(['DRAFT', 'COMPLETE']),
  }),
});

export const createdBearRallySchema = z.object({
  rally: z.object({
    id: z.string(),
    sessionId: z.string(),
    wave: z.number().nullable(),
    sequence: z.number().nullable(),
    leaderPlayerId: z.string(),
    memberCount: z.number(),
    context: z.array(boundContextSchema),
    warnings: z.array(z.string()),
  }),
});

export const bearResultsAppliedSchema = z.object({
  results: z.object({
    sessionId: z.string(),
    ralliesUpdated: z.number(),
    membersUpdated: z.number(),
    entryStatus: z.enum(['DRAFT', 'COMPLETE']),
    warnings: z.array(z.string()),
  }),
});

/**
 * `empty: true` means nothing has been recorded for this player yet.
 *
 * That is a genuinely different state from "analyzed and scored zero", and the
 * UI must say so rather than render zeros that look like measured results.
 */
export const bearAnalysisEnvelopeSchema = z.discriminatedUnion('empty', [
  z.object({
    empty: z.literal(false),
    analysis: personalBearAnalysisSchema,
  }),
  z.object({
    empty: z.literal(true),
    /**
     * Explicitly null, never a zeroed analysis object. A player with no
     * recorded Bear data has an UNKNOWN lead-vs-join answer, not a measured
     * one -- and zeros would render identically to "measured, scored zero".
     */
    analysis: z.null(),
    message: z.string(),
  }),
]);

/**
 * Declared Bear loadouts (spec section 24.4).
 *
 * This records INTENT -- what a player meant to bring -- and is deliberately
 * never derived from `bear_rally_members`, which records the OBSERVATION of
 * what actually marched. The gap between the two ("planned 0/20/80, sent
 * whatever had finished healing") is the entire reason the table exists;
 * populating it from observations would make it a duplicate that still claimed
 * to describe intent.
 *
 * So a row exists only where a human declared one. An undeclared troop split
 * stays null rather than becoming zero.
 */
export const storedLoadoutSchema = z.object({
  playerId: z.string(),
  playerName: z.string().nullable(),
  /** MIXED when the operator did not state a role -- never guessed. */
  role: z.enum(['LEAD', 'JOIN', 'MIXED']),
  heroIds: z.array(z.number()),
  troops: z.object({
    infantry: z.number().nullable(),
    cavalry: z.number().nullable(),
    archer: z.number().nullable(),
  }),
  deploymentCapacity: z.number().nullable().optional(),
  activeBuffs: z.record(z.unknown()),
  notes: z.string().nullable().optional(),
});

export const loadoutListResponseSchema = z.object({
  sessionId: z.string(),
  loadouts: z.array(storedLoadoutSchema),
});

export const loadoutsSavedResponseSchema = z.object({
  sessionId: z.string(),
  saved: z.number(),
  warnings: z.array(z.string()),
});

export const putLoadoutsRequestSchema = z.object({
  loadouts: z
    .array(
      z.object({
        playerId: z.string(),
        role: z.enum(['LEAD', 'JOIN', 'MIXED']).optional(),
        heroIds: z.array(z.number()).max(3).optional(),
        troops: z
          .object({
            infantry: z.number().int().min(0).nullable().optional(),
            cavalry: z.number().int().min(0).nullable().optional(),
            archer: z.number().int().min(0).nullable().optional(),
          })
          .optional(),
        deploymentCapacity: z.number().int().min(0).nullable().optional(),
        activeBuffs: z.record(z.unknown()).optional(),
        notes: z.string().optional(),
      }),
    )
    .min(1)
    .max(200),
});

// ---------------------------------------------------------------------------
// Persisted plans (spec sections 52, 73)
// ---------------------------------------------------------------------------

/**
 * A stored plan, tagged by whether it still matches the current plan schema.
 *
 * Spec section 52 makes every generated plan reproducible and section 53
 * forbids changing scoring behaviour under an existing model version -- which
 * together mean `recommendation_runs` accumulates rows written by schema
 * versions we have since moved past. Pinning the type without acknowledging
 * that would make reading an old plan a parse crash.
 *
 * So the format is explicit. CURRENT carries a fully typed plan the UI renders
 * normally; LEGACY carries the untouched payload plus the model version that
 * produced it, so the UI can say "generated by bear-wave-v1, no longer
 * renderable" instead of either crashing or silently showing nothing.
 */
export const storedBearPlanSchema = z.discriminatedUnion('format', [
  z.object({ format: z.literal('CURRENT'), plan: bearPlanResponseSchema }),
  z.object({
    format: z.literal('LEGACY'),
    modelVersion: z.string(),
    raw: z.unknown(),
  }),
]);

export const persistedBearPlanSchema = z.object({
  id: z.string(),
  modelVersion: z.string(),
  ruleVersion: z.string().nullable(),
  confidence: z.number().nullable(),
  createdAt: z.string(),

  /** The engine's output. Never overwritten by an edit (spec section 73). */
  generated: storedBearPlanSchema,

  manual: z
    .object({
      plan: storedBearPlanSchema,
      note: z.string().optional(),
      editedBy: z.string().nullable(),
      editedAt: z.string(),
    })
    .nullable(),

  manuallyEdited: z.boolean(),

  /**
   * manual when edited, otherwise generated. This is what the alliance should
   * actually follow, and what the UI displays. Show `generated` alongside it
   * when `manuallyEdited` is true so an R4 can see what they overrode.
   */
  effective: storedBearPlanSchema,
});

export const bearCapabilitiesRecalculatedSchema = z.object({
  capabilities: z.array(
    z.object({
      playerId: z.string(),
      playerName: z.string().nullable(),
      bearLead: scoreBreakdownSchema,
      bearJoin: scoreBreakdownSchema,
      reliability: scoreBreakdownSchema,
      cycleEfficiency: z.number().nullable(),
      leadSamples: z.number(),
      joinSamples: z.number(),
      modelVersionLead: z.string(),
      modelVersionJoin: z.string(),
      sourceDataAt: z.string().nullable(),
    }),
  ),
});

export type EventDto = z.infer<typeof eventSchema>;
export type EventParticipantDto = z.infer<typeof eventParticipantSchema>;
export type EventListResponse = z.infer<typeof eventListResponseSchema>;
export type EventDetailResponse = z.infer<typeof eventDetailResponseSchema>;
export type CreateEvent = z.infer<typeof createEventSchema>;
export type RallyRecommendRequest = z.infer<typeof rallyRecommendRequestSchema>;
export type RallyLabResult = z.infer<typeof rallyLabResultSchema>;
export type RallyRecommendResponse = z.infer<typeof rallyRecommendResponseSchema>;
export type BoundContext = z.infer<typeof boundContextSchema>;
export type CreatedBearRally = z.infer<typeof createdBearRallySchema>;
export type StoredBearPlan = z.infer<typeof storedBearPlanSchema>;
export type BearAnalysisEnvelope = z.infer<typeof bearAnalysisEnvelopeSchema>;
export type StoredLoadout = z.infer<typeof storedLoadoutSchema>;
export type LoadoutListResponse = z.infer<typeof loadoutListResponseSchema>;
export type PutLoadoutsRequest = z.infer<typeof putLoadoutsRequestSchema>;
export type PersistedBearPlan = z.infer<typeof persistedBearPlanSchema>;
export type BearCapabilitiesRecalculated = z.infer<typeof bearCapabilitiesRecalculatedSchema>;

// ---------------------------------------------------------------------------
// Bear Trap days (spec sections 19-20, 24, 30)
// ---------------------------------------------------------------------------

/**
 * A Bear Trap DAY: one calendar date carrying both traps.
 *
 * The day is the unit because the choice between the traps is exclusive.
 * `strategy_rules BEAR_TRAP/PARTICIPATION_COOLDOWN` records a ~48h
 * participation cooldown and both traps run the same day, so a member is in
 * BT1, or BT2, or neither. Modelling each trap as its own independent session
 * made that impossible to express, which is why the day exists at all.
 */
export const bearTrapTeamSchema = z.enum(['BT1', 'BT2']);
export const bearTrapNumberSchema = z.union([z.literal(1), z.literal(2)]);

/** A per-day lead decision. Distinct from a standing player override. */
export const bearLeadPreferenceSchema = z.enum(['AUTO', 'FORCE_LEAD', 'DO_NOT_LEAD']);

export const bearTrapSummarySchema = z.object({
  trapNumber: bearTrapNumberSchema,
  team: bearTrapTeamSchema,
  /** Null until the day's session for this trap exists. */
  sessionId: z.string().nullable(),
  startedAt: z.string().nullable(),
  entryStatus: z.enum(['DRAFT', 'COMPLETE']).nullable(),
  assignedCount: z.number(),
  /** Counted from recorded rallies, never read from a stored total. */
  rallyCount: z.number(),
  leadCount: z.number(),
});

export const bearDaySummarySchema = z.object({
  eventId: z.string(),
  allianceId: z.string().nullable(),
  /** YYYY-MM-DD in the operator's own reckoning, not a UTC cast. */
  date: z.string().nullable(),
  name: z.string().nullable(),
  status: z.enum(['PLANNED', 'OPEN', 'IN_PROGRESS', 'COMPLETED', 'CANCELLED']),
  createdAt: z.string(),
  traps: z.array(bearTrapSummarySchema),
  assignedTotal: z.number(),
});

export const bearDayAssignmentSchema = z.object({
  playerId: z.string(),
  playerName: z.string().nullable(),
  /**
   * Null when a row exists with a `team` this app does not recognise -- an
   * event edited elsewhere, say. Rendered as unassigned rather than silently
   * placed in a trap.
   */
  trapNumber: bearTrapNumberSchema.nullable(),
  team: z.string().nullable(),
  leadPreference: bearLeadPreferenceSchema,
  notes: z.string().nullable(),
});

export const bearDayDetailResponseSchema = z.object({
  day: bearDaySummarySchema,
  assignments: z.array(bearDayAssignmentSchema),
});

export const bearDayListResponseSchema = z.object({ days: z.array(bearDaySummarySchema) });
export const bearDayResponseSchema = z.object({ day: bearDaySummarySchema });

export const createBearDaySchema = z.object({
  allianceId: z.string().regex(/^\d+$/),
  /** Calendar date, not a timestamp: both traps share it. */
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use a YYYY-MM-DD date.'),
  name: z.string().max(200).optional(),
  /** Optional per-trap start times, keyed "1" and "2". */
  trapStartsAt: z.record(z.string()).optional(),
});

/**
 * The whole assignment for a day, replacing whatever is stored.
 *
 * A player with `trapNumber: null` is removed rather than recorded as being in
 * no trap: not taking part is the absence of a decision about which trap, and
 * a row saying "neither" would claim more than the operator said.
 */
export const setBearAssignmentsSchema = z.object({
  assignments: z
    .array(
      z.object({
        playerId: z.string().regex(/^\d+$/),
        trapNumber: bearTrapNumberSchema.nullable(),
        leadPreference: bearLeadPreferenceSchema.optional(),
        notes: z.string().max(2000).nullable().optional(),
      }),
    )
    .max(400),
});

export const bearAssignmentsSavedSchema = z.object({
  eventId: z.string(),
  assigned: z.number(),
  removed: z.number(),
  byTrap: z.record(z.number()),
  warnings: z.array(z.string()),
});

/**
 * How big to run one trap, given who is assigned to it.
 *
 * Separate from the wave plan because it has to be answerable BEFORE anyone is
 * placed: the operator decides the shape, then the planner fills it. Every
 * figure carries its own null, and `recommendedLeadCount` in particular is null
 * rather than guessed when no trap has been recorded to anchor it.
 */
export const bearTrapSizingSchema = z.object({
  trapNumber: bearTrapNumberSchema,
  participantCount: z.number(),
  leadCandidateCount: z.number(),

  recommendedLeadCount: z.number().nullable(),
  recommendedWaveCount: z.number().nullable(),
  recommendedRalliesPerWave: z.number().nullable(),

  launchesPerLead: z.number().nullable(),
  totalLaunches: z.number().nullable(),
  lastOpeningMinutesBeforeEnd: z.number().nullable(),

  observedLeadCount: z.number().nullable(),
  observedLeadSource: z.enum(['RECORDED_RALLIES', 'STRATEGY_RULES']).nullable(),
  observedLeadDetail: z.string().nullable(),

  joinerFillsPerCycle: z.number().nullable(),
  /** The per-rally joiner count this roster can still keep full. */
  maxJoinerSlotsSupported: z.number().nullable(),

  throughputRegime: z.string().nullable(),

  basis: z.array(z.string()),
  unknowns: z.array(z.string()),
  confidence: z.number().min(0).max(1),
});

export const bearTrapSizingResponseSchema = z.object({
  sizing: bearTrapSizingSchema,
  sessionId: z.string().nullable(),
});

/**
 * Planning one trap of a day.
 *
 * The roster is NOT sent: it is whoever is assigned to this trap, which the
 * server already holds. Sending it would let the screen plan around a roster
 * nobody saved, and the saved assignment is the thing the alliance acts on.
 */
export const bearTrapPlanRequestSchema = z.object({
  plannerMode: z.enum(['MAX_ALLIANCE_DAMAGE', 'BALANCED_REWARDS', 'PERSONAL_SCORE_ASSIST']),
  focusPlayerId: z.string().optional(),
  waveCount: z.number().int().min(1).max(10).optional(),
  ralliesPerWave: z.number().int().min(1).max(20).optional(),
});

export type BearTrapTeam = z.infer<typeof bearTrapTeamSchema>;
export type BearTrapNumber = z.infer<typeof bearTrapNumberSchema>;
export type BearLeadPreference = z.infer<typeof bearLeadPreferenceSchema>;
export type BearTrapSummary = z.infer<typeof bearTrapSummarySchema>;
export type BearDaySummary = z.infer<typeof bearDaySummarySchema>;
export type BearDayAssignment = z.infer<typeof bearDayAssignmentSchema>;
export type BearDayDetailResponse = z.infer<typeof bearDayDetailResponseSchema>;
export type BearDayListResponse = z.infer<typeof bearDayListResponseSchema>;
export type BearDayResponse = z.infer<typeof bearDayResponseSchema>;
export type CreateBearDay = z.infer<typeof createBearDaySchema>;
export type SetBearAssignments = z.infer<typeof setBearAssignmentsSchema>;
export type BearAssignmentsSaved = z.infer<typeof bearAssignmentsSavedSchema>;
export type BearTrapSizing = z.infer<typeof bearTrapSizingSchema>;
export type BearTrapSizingResponse = z.infer<typeof bearTrapSizingResponseSchema>;
export type BearTrapPlanRequest = z.infer<typeof bearTrapPlanRequestSchema>;
